import React, { useState, useEffect } from 'react';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { GuidedMethodology, GuidedMethodologyStep, StepCondition } from "../../lib/firebase";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { X } from "lucide-react";

interface GuidedMethodologyEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { name: string; description: string; status: 'Active' | 'Inactive' | 'Draft'; keywords?: string[] }) => void;
  name?: string;
  description?: string;
  status?: 'Active' | 'Inactive' | 'Draft';
  keywords?: string[];
  isNew?: boolean;
}

export const GuidedMethodologyEditDialog: React.FC<GuidedMethodologyEditDialogProps> = ({
  open,
  onClose,
  onSave,
  name: initialName = '',
  description: initialDescription = '',
  status: initialStatus = 'Draft',
  keywords: initialKeywords = [],
  isNew = false
}) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [status, setStatus] = useState<'Active' | 'Inactive' | 'Draft'>(initialStatus);
  const [keywords, setKeywords] = useState<string[]>(initialKeywords || []);
  const [newKeyword, setNewKeyword] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens with new values
  useEffect(() => {
    if (open) {
      console.log("Dialog opened with props:", { 
        initialName, 
        initialDescription, 
        initialStatus, 
        initialKeywords 
      });
      
      setName(initialName || '');
      setDescription(initialDescription || '');
      setStatus(initialStatus || 'Draft');
      setKeywords(Array.isArray(initialKeywords) ? initialKeywords : []);
      setNewKeyword('');
      setError(null);
      
      console.log("State after setting:", {
        name: initialName,
        description: initialDescription,
        status: initialStatus,
        keywords: Array.isArray(initialKeywords) ? initialKeywords : []
      });
    }
  }, [open, initialName, initialDescription, initialStatus, initialKeywords]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    
    const result = { 
      name: name.trim(), 
      description: description.trim(), 
      status: status as 'Active' | 'Inactive' | 'Draft',
      keywords: keywords 
    };
    
    console.log("Submitting data from dialog:", result);
    onSave(result);
  };

  const handleAddKeyword = (e?: React.MouseEvent) => {
    e?.preventDefault();
    
    if (!newKeyword.trim()) return;
    
    // Don't add duplicates
    if (keywords.includes(newKeyword.trim().toLowerCase())) {
      setError("Keyword already exists");
      return;
    }

    setKeywords([...keywords, newKeyword.trim().toLowerCase()]);
    setNewKeyword('');
    setError(null);
  };

  const handleRemoveKeyword = (keywordToRemove: string, e?: React.MouseEvent) => {
    e?.preventDefault();
    setKeywords(keywords.filter(keyword => keyword !== keywordToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleStatusChange = (value: string) => {
    setStatus(value as 'Active' | 'Inactive' | 'Draft');
  };

  return (
    <Dialog open={open} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]" aria-describedby="methodology-edit-description">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Create New Guided Methodology' : 'Edit Guided Methodology'}</DialogTitle>
          <DialogDescription id="methodology-edit-description">
            {isNew ? 'Create a new guided methodology' : 'Make changes to your guided methodology'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
              <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
                placeholder="Enter methodology name"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter methodology description"
              rows={3}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select 
              value={status} 
              onValueChange={handleStatusChange}
            >
              <SelectTrigger id="status">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
                <SelectItem value="Draft">Draft</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              {status === 'Active' ? 'Will be visible to all users' : 
               status === 'Inactive' ? 'Hidden from users but can be enabled later' : 
               'Only visible to admins, still in development'}
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="keywords">Keywords</Label>
            <div className="flex gap-2">
              <Input
                id="keywords"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add keyword"
                className="flex-1"
              />
              <Button
                type="button"
                onClick={handleAddKeyword}
                variant="outline"
              >
                Add
              </Button>
            </div>
            
            {error && <p className="text-sm text-red-500">{error}</p>}
            
            <div className="flex flex-wrap gap-2 mt-2">
              {keywords.map((keyword, index) => (
                <Badge 
                  key={index} 
                  variant="secondary"
                  className="flex items-center gap-1 px-2 py-1"
                >
                  {keyword}
                  <X 
                    size={14} 
                    className="cursor-pointer" 
                    onClick={(e) => handleRemoveKeyword(keyword, e)}
                  />
                </Badge>
              ))}
              {keywords.length === 0 && (
                <p className="text-sm text-gray-500">No keywords added</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {isNew ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 